/**
 * Computes default/initial values for the Details step form.
 * Keys MUST match the field `name` in useDetailsFormSchema.
 */
import { useMemo } from 'react';
import { useLocationFieldConfig } from '@/formConfigs';
import type {
  ScheduleMode,
  ApiReference,
} from '../../../config/schedule.types';

export interface CalendarInfoFieldValue {
  provider_calendar_id: string;
  is_primary?: boolean;
}

export interface PhoneValue {
  countryCode: string;
  dialCode: string;
  number: string;
}

export interface DetailsFormValues {
  [key: string]: unknown;
  interviewName: string;
  interview_type: string;
  participant_ids: string | string[];
  book_meeting_room: boolean;
  meeting_room_ids: string[];
  calendar_info: CalendarInfoFieldValue | null;
  duration: number;
  dateTime: string;
  startDate: Date | null;
  startTime: string;
  endTime: string;
  isDateTimeEditing: string;
  isCalendarEditing: string;
  /** String companion to `calendar_info` — feeds the native edit Select. */
  calendar_info_picker: string;
  findTime: string;
  calendar_visibility: boolean;
  location: string;
  host_id: string;
  ats_office_id: string;
  candidatePhone: string;
  candidate_phone: PhoneValue | undefined;
  isCandidatePhoneEditing: string;
  note_taker_enabled: boolean;
  fill_with_ai_enabled: boolean;
  ext_interview_template_id: string | null;
}

interface UseDetailsFormDefaultsOptions {
  candidatePhoneValue?: PhoneValue;
}

export function useDetailsFormDefaults(
  interviewPlan: Record<string, unknown> | undefined,
  mode: ScheduleMode,
  apiReference: ApiReference,
  options?: UseDetailsFormDefaultsOptions
): DetailsFormValues {
  const isEdit = apiReference === 'edit';
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  const candidatePhoneValue = options?.candidatePhoneValue;

  const { defaultLocation: availableDefault } = useLocationFieldConfig({});

  return useMemo(() => {
    const plan = interviewPlan ?? {};

    const interviewName =
      isAdhoc && !isEdit
        ? ''
        : ((plan.substituted_name as string) ?? (plan.name as string) ?? '');

    const interview_type =
      (plan.kind as string) ?? (plan.interview_kind as string) ?? 'one_on_one';

    // `/ats_interviews/:id` (edit) emits top-level `participants`;
    // `/ats_interviews/new` (create plan, after flatten) emits
    // `interviewers` — accept either so both modes hydrate the list.
    interface PlanParticipant {
      id?: number;
      name?: string;
      email?: string;
      avatar?: string;
      avatar_url?: string;
      avatar_thumb_url?: string;
      /** Backend emits snake_case; some flattened views emit camel. */
      time_zone?: string;
      timezone?: string;
    }
    const participants: PlanParticipant[] = Array.isArray(plan.participants)
      ? (plan.participants as PlanParticipant[])
      : Array.isArray(plan.interviewers)
        ? (plan.interviewers as PlanParticipant[])
        : [];
    const interviewerIds = participants
      .map((p) => String(p.id))
      .filter(Boolean);
    // Panel + self-schedule are multi-select (array); one-on-one is
    // single (scalar). For a panel plan we need the whole list so the
    // schema's multi-select hydrates with every pool member; taking
    // only the first left the field in single-mode even though the
    // type was "Panel interview".
    const isPanel = interview_type === 'panel';
    const participant_ids =
      isSelfSchedule || isPanel ? interviewerIds : (interviewerIds[0] ?? '');
    // Pre-resolved option objects so the async select renders the
    // chosen interviewer's name/avatar on mount without fetching by id.
    // `data.timezone` is required so `renderTriggerValue` can draw the
    // "10:30AM PST" clock on the chip in edit mode — without it the
    // hydrated option has no tz and the clock silently hides.
    const participant_default_options = participants
      .filter((p) => p.id !== undefined)
      .map((p) => ({
        value: String(p.id),
        label: p.name ?? p.email ?? `User ${p.id}`,
        description: p.email,
        image: p.avatar ?? p.avatar_url ?? p.avatar_thumb_url,
        data: { timezone: p.time_zone ?? p.timezone ?? '' },
      }));

    const duration =
      typeof plan.duration === 'number' && plan.duration > 0
        ? plan.duration
        : 30;

    // Smart location fallback: plan value > first available from account settings
    const planLocation = plan.location as string | undefined;
    const location =
      planLocation && planLocation !== 'unspecified'
        ? planLocation
        : (availableDefault ?? '');

    const hostObj =
      typeof plan.host === 'object' && plan.host !== null
        ? (plan.host as Record<string, unknown>)
        : null;
    const host_id = hostObj?.id ? String(hostObj.id) : '';

    // Office: plan/edit views emit either flat `ats_office_id` or the
    // nested `ats_office: { id }` — accept both.
    const officeObj =
      typeof plan.ats_office === 'object' && plan.ats_office !== null
        ? (plan.ats_office as Record<string, unknown>)
        : null;
    const rawOfficeId =
      plan.ats_office_id !== undefined && plan.ats_office_id !== null
        ? plan.ats_office_id
        : officeObj?.id;
    const ats_office_id =
      rawOfficeId !== undefined && rawOfficeId !== null
        ? String(rawOfficeId)
        : '';

    const meetingRooms = Array.isArray(plan.meeting_rooms)
      ? (plan.meeting_rooms as Array<{ email?: string }>)
          .map((r) => r.email ?? '')
          .filter(Boolean)
      : [];
    const book_meeting_room = meetingRooms.length > 0;

    const planCal = plan.calendar_info as
      | CalendarInfoFieldValue
      | null
      | undefined;
    const scheduled = plan.scheduled_calendar as
      | {
          provider_calendar_id?: string;
          is_primary?: boolean;
        }
      | undefined;

    let calendar_info: CalendarInfoFieldValue | null = null;
    if (isEdit && scheduled?.provider_calendar_id) {
      calendar_info = {
        provider_calendar_id: scheduled.provider_calendar_id,
        is_primary: Boolean(scheduled.is_primary),
      };
    } else if (planCal?.provider_calendar_id) {
      calendar_info = {
        provider_calendar_id: planCal.provider_calendar_id,
        is_primary: Boolean(planCal.is_primary),
      };
    }

    const calendar_visibility =
      plan.calendar_visibility === 'private' ||
      plan.calendar_visibility === true;
    const note_taker_enabled = Boolean(plan.note_taker_enabled);
    const fill_with_ai_enabled = Boolean(plan.fill_with_ai_enabled ?? true);

    // HackerRank template: plan exposes it nested as
    // `hackerrank_interview.ext_interview_template_id` (current API) or
    // top-level (legacy fallback). Keep as string for select parity.
    const hrNested = plan.hackerrank_interview as
      | { ext_interview_template_id?: string | number }
      | undefined;
    const hrTemplate =
      hrNested?.ext_interview_template_id ??
      (plan.ext_interview_template_id as string | number | undefined);
    const ext_interview_template_id =
      hrTemplate !== undefined && hrTemplate !== null
        ? String(hrTemplate)
        : null;

    const defaults: DetailsFormValues = {
      interviewName,
      interview_type,
      participant_ids,
      participant_default_options,
      book_meeting_room,
      meeting_room_ids: meetingRooms,
      calendar_info,
      duration,
      dateTime: '',
      startDate: null,
      startTime: '',
      endTime: '',
      isDateTimeEditing: 'view',
      isCalendarEditing: 'view',
      calendar_info_picker: calendar_info?.provider_calendar_id ?? '',
      findTime: '',
      calendar_visibility,
      location,
      host_id,
      ats_office_id,
      candidatePhone: '',
      // Phone adapter rejects `null`; use `undefined` when no phone on
      // record so its validator doesn't emit "Expected undefined,
      // received null".
      candidate_phone: candidatePhoneValue ?? undefined,
      // When the candidate has no phone on record, start in edit mode
      // so the phone input renders directly (no "No phone on record"
      // info block — just collect the number). When there IS a phone,
      // start in view mode: read-only display with a pencil to edit.
      isCandidatePhoneEditing: candidatePhoneValue ? 'view' : 'edit',
      note_taker_enabled,
      fill_with_ai_enabled,
      ext_interview_template_id,
    };
    return defaults;
  }, [
    interviewPlan,
    isEdit,
    isAdhoc,
    isSelfSchedule,
    availableDefault,
    candidatePhoneValue,
  ]);
}
